// src/client/lib/query-keys.ts

/**
 * Type-safe Query Key Factory für React Query
 * Verhindert Tippfehler und zentralisiert Query Keys
 */

import type { ProfileFilters, GetSelectorsParams } from '@client/types';

export const selectorsKeys = {
  all: () => ['/selectors'] as const,
  lists: () => [...selectorsKeys.all(), 'list'] as const,
  list: (filters?: string) => [...selectorsKeys.lists(), { filters }] as const,
  details: () => [...selectorsKeys.all(), 'detail'] as const,
  detail: (id: number) => [...selectorsKeys.details(), id] as const,
  byCategory: (category: string) => [...selectorsKeys.all(), 'category', category] as const,
};

// ============================================================================
// Query Key Factory Pattern
// ============================================================================

export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    status: () => [...queryKeys.auth.all, 'status'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
    sessions: () => [...queryKeys.auth.all, 'sessions'] as const,
    stats: () => [...queryKeys.auth.all, 'stats'] as const,
    checkUsername: (username: string) => 
      [...queryKeys.auth.all, 'check-username', username] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.users.all, 'detail', id] as const,
  },

  // Settings
  settings: {
    all: () => ['settings'] as const,
    lists: () => [...queryKeys.settings.all(), 'list'] as const,
    detail: (key: string) => [...queryKeys.settings.all(), 'detail', key] as const,
    byPrefix: (prefix: string) => 
      [...queryKeys.settings.all(), 'prefix', prefix] as const,
  },

  // Lists
  lists: {
    all: () => ['lists'] as const,
    lists: () => [...queryKeys.lists.all(), 'list'] as const,
    detail: (id: number) => [...queryKeys.lists.all(), 'detail', id] as const,
  },

  // Profiles
  profiles: {
    all: ['profiles'] as const,
    lists: () => [...queryKeys.profiles.all, 'list'] as const,
    byList: (listId: number) => 
      [...queryKeys.profiles.all, 'by-list', listId] as const,
    filtered: (filters: ProfileFilters) => 
      [...queryKeys.profiles.all, 'filtered', filters] as const,
    detail: (id: number) => 
      [...queryKeys.profiles.all, 'detail', id] as const,
  },

  // Profile Categories
  profileCategories: {
    all: ['profile-categories'] as const,
    lists: () => [...queryKeys.profileCategories.all, 'list'] as const,
    detail: (id: number) => 
      [...queryKeys.profileCategories.all, 'detail', id] as const,
  },

  // Selectors
  selectors: {
    all: ['selectors'] as const,
    lists: () => [...selectorsKeys.all(), 'list'] as const,
    list: (filters?: string) => [...selectorsKeys.lists(), { filters }] as const,
    details: () => [...selectorsKeys.all(), 'detail'] as const,
    detail: (id: number) => [...selectorsKeys.details(), id] as const,
    byCategory: (category: string) => [...selectorsKeys.all(), 'category', category] as const,
  },
    

  // Conversations
  conversations: {
    all: ['conversations'] as const,
    lists: () => [...queryKeys.conversations.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.conversations.all, 'detail', id] as const,
  },

  // Admin
  admin: {
    all: ['admin'] as const,
    tables: () => [...queryKeys.admin.all, 'tables'] as const,
    tableDetails: (tableName: string) => 
      [...queryKeys.admin.all, 'table-details', tableName] as const,
    sampleData: (tableName: string, limit: number = 5) => 
      [...queryKeys.admin.all, 'sample-data', tableName, limit] as const,
    search: (tableName: string, searchTerm: string) => 
      [...queryKeys.admin.all, 'search', tableName, searchTerm] as const,
    databaseInfo: () => [...queryKeys.admin.all, 'database-info'] as const,
  },

  // System
  system: {
    all: ['system'] as const,
    health: () => [...queryKeys.system.all, 'health'] as const,
    stats: () => [...queryKeys.system.all, 'stats'] as const,
    storageTest: () => [...queryKeys.system.all, 'storage-test'] as const,
  },
} as const;

// ============================================================================
// Type-safe Invalidation Helpers
// ============================================================================

export const invalidationHelpers = {
  // Invalidate all queries for a resource
  invalidateUsers: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
  },

  invalidateSettings: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
  },

  invalidateLists: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.lists.all });
  },

  invalidateProfiles: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.profiles.all });
  },

  invalidateProfileCategories: (queryClient: any) => {
    return queryClient.invalidateQueries({ 
      queryKey: queryKeys.profileCategories.all 
    });
  },

  invalidateSelectors: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.selectors.all });
  },

  invalidateConversations: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
  },

  invalidateAdmin: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
  },

  // Invalidate specific table data
  invalidateTable: (queryClient: any, tableName: string) => {
    return queryClient.invalidateQueries({ 
      queryKey: queryKeys.admin.tableDetails(tableName)
    });
  },

  // Invalidate multiple resources at once
  invalidateMultiple: (queryClient: any, keys: readonly string[][]) => {
    return Promise.all(
      keys.map(key => queryClient.invalidateQueries({ queryKey: key }))
    );
  },
};

// ============================================================================
// Prefetch Helpers
// ============================================================================

export const prefetchHelpers = {
  prefetchUsers: async (queryClient: any, fetcher: () => Promise<any>) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.users.lists(),
      queryFn: fetcher,
    });
  },

  prefetchSettings: async (queryClient: any, fetcher: () => Promise<any>) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.settings.lists(),
      queryFn: fetcher,
    });
  },

  prefetchStats: async (queryClient: any, fetcher: () => Promise<any>) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.system.stats(),
      queryFn: fetcher,
    });
  },
};

// ============================================================================
// Type Exports für externe Nutzung
// ============================================================================

export type QueryKeys = typeof queryKeys;
export type AuthQueryKeys = typeof queryKeys.auth;
export type UserQueryKeys = typeof queryKeys.users;
export type SettingQueryKeys = typeof queryKeys.settings;
export type ListQueryKeys = typeof queryKeys.lists;
export type ProfileQueryKeys = typeof queryKeys.profiles;
export type SelectorQueryKeys = typeof queryKeys.selectors;
export type AdminQueryKeys = typeof queryKeys.admin;
export type SystemQueryKeys = typeof queryKeys.system;